import { SectionHeader } from '@react/components/common/SectionHeader'
import React, { FC, useEffect, useState } from 'react'

import { OtStoreState, useOtChainState, useOtChainTimestamp, useOtSelector } from '@react/redux/store'

import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import { AddressPill } from '@react/components/addresses/AddressPill'
import ButtonModal from '@react/components/ButtonModal'
import { ETH_ADDR_ZERO, EthAddr } from '@shared/SchemaTypes'
import { useParams } from 'react-router-dom'

import ViewNumber from '@react/components/elements/ViewNumber'
import { erc20WeiToCommaDecString, timestampToUTCDateTimeString } from '@shared/conversions'

import { ethers } from 'ethers'

import { RoleEnum } from '@shared/Enums'
import AdminRepayFlexForm from './AdminRepayFlexForm'

import { EventTypeForTemplate } from '@shared/templateApx'

import BorrowerTransferForRepayFlexForm from '@react/borrower/BorrowerTransferForRepayFlexForm'
import {
  fetchErc20BalanceForPool,
  fetchPoolFlexWithdrawActionPoolEventsCTOs,
  scanPoolsForEvents,
} from '@react/fetchers/PoolFetchers'
import { PoolFlexAbi } from '@shared/Abis'
import { CompanyProfileCTO } from '@shared/CompanyProfileCTO'
import { logOT } from '@shared/loggingShared'
import { PoolEventCTO, PoolFlexOverviewCTO, PoolFlexOverviewCTOSchema } from '@shared/sharedTypes'
import { IPoolLenderWithdrawEventStructOutput } from '@typechain-types/contracts/PoolFlex'

export const AdminBorrowerWithdrawalsFlexList: FC<{ poolAddr?: EthAddr }> = (props) => {
  const p = useParams()?.poolAddr
  const poolAddr = (props.poolAddr ? props.poolAddr : p) as EthAddr

  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    userConnection: { roleEnum, signer },
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()
  const companyProfiles = useOtSelector((state: OtStoreState) => state.poolState.companyProfiles)

  const chainTimestamp = useOtChainTimestamp()

  const theme = useTheme()

  const poolFlexOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO] as PoolFlexOverviewCTO
  )
  const [borrowerVaultBalance, setBorrowerVaultBalance] = React.useState<number>(0)
  const [poolBalance, setPoolBalance] = React.useState<number>(0)

  const [withdrawEventStructs, setWithdrawEventStructs] = useState<IPoolLenderWithdrawEventStructOutput[]>([])
  const [poolEventCTOs, setPoolEventCTOs] = useState<PoolEventCTO[]>([])

  useEffect(() => {
    async function fetchWithdraws(): Promise<void> {
      if (providerReadOnly && signer && serverApiBaseUrl && restAPIKey && poolAddr) {
        logOT.info('fetchWithdraws1')
        await scanPoolsForEvents(serverApiBaseUrl, restAPIKey, poolAddr)
        const poolEvents: PoolEventCTO[] = await fetchPoolFlexWithdrawActionPoolEventsCTOs(
          serverApiBaseUrl,
          restAPIKey,
          poolAddr
        )
        const poolFlexContract = new ethers.Contract(poolAddr, PoolFlexAbi, providerReadOnly)

        const withdrawEvents = (await poolFlexContract.withdrawEvents()) as IPoolLenderWithdrawEventStructOutput[]

        const today = chainTimestamp - (chainTimestamp % (3600 * 24))

        let todayAmount = 0
        let pastAmount = 0

        logOT.info('repay events', poolEvents)

        withdrawEvents.forEach((event) => {
          if (Number(event.transferOutDayTimestamp) === today) {
            todayAmount = todayAmount + Number(event.requestedAssets)
          }
          if (Number(event.transferOutDayTimestamp ?? 0) < today) {
            pastAmount = pastAmount + Number(event.requestedAssets ?? 0)
          }
        })
        setWithdrawEventStructs(withdrawEvents)
        setPoolEventCTOs(poolEvents)
      }
    }

    fetchWithdraws().catch((err) => console.error(err))
  }, [poolFlexOverviewCTO])

  useEffect(() => {
    if (poolFlexOverviewCTO) {
      const borrowerVaultAddr = poolFlexOverviewCTO.borrowerVaultAddr

      fetchErc20BalanceForPool(providerReadOnly, poolAddr, poolAddr)
        .then((b) => {
          setPoolBalance(Number(b))
        })
        .catch((e) => {
          logOT.error(e)
        })

      fetchErc20BalanceForPool(providerReadOnly, borrowerVaultAddr, poolAddr)
        .then((b) => {
          setBorrowerVaultBalance(Number(b))
        })
        .catch((e) => {
          logOT.error(e)
        })
    }
  }, [poolFlexOverviewCTO])

  const getCompanyProfile = (userAddr: EthAddr): CompanyProfileCTO => {
    const companyProfile = companyProfiles.find((profile) => profile.wallets.includes(userAddr))
    return (
      companyProfile ?? {
        instructions: '',
        companyName: 'Unlisted',
        uuid: '',
        wallets: [],
        emails: [],
      }
    )
  }

  if (!poolFlexOverviewCTO) {
    return <></>
  }
  const liquidityTokenSymbol = poolFlexOverviewCTO.liquidityTokenSymbol

  return (
    <Box sx={{ paddingTop: 2 }}>
      <SectionHeader>Withdrawal Requests</SectionHeader>
      <Box sx={{ padding: 4, marginBottom: 4 }}>
        <Grid
          container
          spacing={2}
          sx={{ borderBottomWidth: 1, borderBottomColor: theme.palette.divider, marginTop: 2 }}
        >
          <Grid item sx={{ marginBottom: 4 }} xs={12} sm={4}>
            <ViewNumber
              card
              toolTip="The amount of Loan Principal that is currently in Withdrawal Requests"
              label="Principal to be Withdrawn"
              inTable={false}
              poolTokenSymbol=""
              liquidityTokenSymbol={liquidityTokenSymbol}
              dataValue={poolFlexOverviewCTO.totalRequestedAssets}
              fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.totalRequestedAssets}
              value={poolFlexOverviewCTO.totalRequestedAssets}
            />
          </Grid>
          <Grid item sx={{ marginBottom: 4 }} xs={12} sm={4}>
            <ViewNumber
              card
              toolTip="The balance of assets in the Borrower's Vault"
              label="Borrower Vault Balance"
              inTable={false}
              poolTokenSymbol=""
              liquidityTokenSymbol={liquidityTokenSymbol}
              dataValue={borrowerVaultBalance.toString()}
              fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.totalRequestedAssets}
              value={borrowerVaultBalance.toString()}
            />
          </Grid>
          <Grid item sx={{ marginBottom: 4 }} xs={12} sm={4}>
            <ViewNumber
              card
              toolTip="Pool Balance"
              label="Pool Balance"
              inTable={false}
              poolTokenSymbol=""
              liquidityTokenSymbol={liquidityTokenSymbol}
              dataValue={poolBalance.toString()}
              fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.totalRequestedAssets}
              value={poolBalance.toString()}
            />
          </Grid>
        </Grid>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="20%">Lender</TableCell>
                <TableCell width="20%">Request Timestamp</TableCell>
                <TableCell width="20%">Request Amount</TableCell>
                <TableCell width="10%">Principal({liquidityTokenSymbol})</TableCell>
                <TableCell width="10%" align="center">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {withdrawEventStructs.map((event: IPoolLenderWithdrawEventStructOutput) => {
                const enoughFundsInVault = Number(event.requestedAssets) <= borrowerVaultBalance
                const enoughFundsInPool = Number(event.requestedAssets) <= poolBalance
                const processedPoolEvent = poolEventCTOs.find((e) => {
                  return (
                    e.eventType === EventTypeForTemplate.RepayToPoolProcessed &&
                    e.withdrawSequenceId === event.eventId.toString()
                  )
                })
                const lenderProfile = getCompanyProfile(event.lender as EthAddr)
                return (
                  <TableRow key={event.eventId.toString()}>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                          {lenderProfile.companyName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AddressPill addr={event.lender as EthAddr} />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="bodyBold">
                        {timestampToUTCDateTimeString(Number(event.requestTimestamp) * 1000)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="bodyBold">{erc20WeiToCommaDecString(event.requestedShares)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="bodyBold">{erc20WeiToCommaDecString(event.requestedAssets)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {(roleEnum === RoleEnum.BORROWER || roleEnum === RoleEnum.POOL_ADMIN) && !processedPoolEvent && (
                        <ButtonModal
                          disabled={roleEnum !== RoleEnum.BORROWER || !enoughFundsInVault}
                          title={'Transfer to pool for Repayment'}
                          variant="primary"
                        >
                          <BorrowerTransferForRepayFlexForm poolAddr={poolAddr} withdrawEvent={event} />
                        </ButtonModal>
                      )}
                      {(roleEnum === RoleEnum.BORROWER || roleEnum === RoleEnum.POOL_ADMIN) && !!processedPoolEvent && (
                        <ButtonModal
                          disabled={roleEnum !== RoleEnum.POOL_ADMIN || !enoughFundsInPool}
                          title={'Send Loan Repayment'}
                          variant="primary"
                        >
                          <AdminRepayFlexForm poolAddr={poolAddr} withdrawEvent={event} />
                        </ButtonModal>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {withdrawEventStructs.length === 0 && (
          <Box sx={{ padding: 4 }}>
            <Typography variant="bodyMuted">No Withdrawal Requests at this time.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}
